import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import styles from "./Welcome.module.css";
import authAPI from "../../api/authAPI";

import {
  doGetFeideLoginUrl,
  doLoginWithPassword,
  doSignup,
  selectFeideUrl,
  selectLoginError,
  selectIsLoggingIn,
} from "../../features/auth/AuthSlice";

import {
  LogoForsknigsradet,
  LogoJaerskulen,
  LogoLesesenteret,
} from "../../common/Logos/Logos";

const Welcome = () => {
  const { t } = useTranslation("common");
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [allowOpenSignup, setAllowOpenSignup] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();
  const feideUrl = useSelector(selectFeideUrl);
  const loginError = useSelector(selectLoginError);
  const isLoggingIn = useSelector(selectIsLoggingIn);

  useEffect(() => {
    document.body.classList.add(styles.background);
    return () => document.body.classList.remove(styles.background);
  }, []);

  useEffect(() => {
    if (!feideUrl) dispatch(doGetFeideLoginUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feideUrl]);

  useEffect(() => {
    async function fetchAuthConfig() {
      try {
        const response = await authAPI.getAuthConfig();
        setAllowOpenSignup(response.data.allowOpenSignup);
      } catch (err) {
        console.log("Could not fetch auth config");
      }
    }
    fetchAuthConfig();
  }, []);

  function handleLogInClick() {
    window.location = feideUrl;
  }

  async function handlePasswordLogin(e) {
    e.preventDefault();
    const result = await dispatch(doLoginWithPassword({ email, password }));
    if (doLoginWithPassword.fulfilled.match(result)) {
      history.push("/framside");
    }
  }

  async function handleSignup(e) {
    e.preventDefault();
    const result = await dispatch(doSignup({ email, fullName, password }));
    if (doSignup.fulfilled.match(result)) {
      history.push("/framside");
    }
  }

  function toggleMode() {
    setIsSignupMode(!isSignupMode);
    setEmail("");
    setFullName("");
    setPassword("");
  }

  return (
    <>
      <main className={styles.main}>
        <h1 className={styles.title}>DigUp</h1>
        <h2 className={styles.subTitle}>{t("welcome.header")}</h2>

        {!isSignupMode ? (
          <form className={styles.loginForm} onSubmit={handlePasswordLogin}>
            <div className={styles.inputGroup}>
              <label htmlFor="email">{t("auth.email")}</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoggingIn}
                className={styles.input}
              />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="password">{t("auth.password")}</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoggingIn}
                className={styles.input}
              />
              <a href="/reset-password" className={styles.forgotPassword}>
                {t("auth.forgotPassword")}
              </a>
            </div>

            {loginError && (
              <div className={styles.error}>
                {loginError.message || t("auth.loginFailed")}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <button
                type="submit"
                className={styles.button}
                style={{minWidth: 275}}
                disabled={isLoggingIn}
              >
                {isLoggingIn ? t("loggingIn") : t("auth.loginWithPassword")}
              </button>
              {allowOpenSignup && (
                <button
                  type="button"
                  onClick={toggleMode}
                  className={styles.toggleMode}
                >
                  {t("auth.signup.title")}
                </button>
              )}
              <div className={styles.divider}>{t("auth.or")}</div>

              <button
                className={styles.button}
                disabled={!feideUrl || isLoggingIn}
                style={{minWidth: 275}}
                onClick={handleLogInClick}
              >
                {t("welcome.logInWith")}{" "}
                <img
                  src="/images/feide-logo.png"
                  alt="Feide"
                  width="92"
                  height="32"
                ></img>
              </button>
            </div>
          </form>
        ) : (
          <form className={styles.loginForm} onSubmit={handleSignup}>
            <div className={styles.inputGroup}>
              <label htmlFor="fullName">{t("auth.signup.fullName")}</label>
              <input
                type="text"
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                disabled={isLoggingIn}
                className={styles.input}
              />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="email">{t("auth.email")}</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoggingIn}
                className={styles.input}
              />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="password">{t("auth.password")}</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoggingIn}
                className={styles.input}
                minLength={8}
              />
            </div>

            {loginError && (
              <div className={styles.error}>
                {loginError.message || t("auth.signup.signupFailed")}
              </div>
            )}

            <button
              type="submit"
              className={styles.button}
              disabled={isLoggingIn}
            >
              {isLoggingIn
                ? t("auth.signup.signingUp")
                : t("auth.signup.signupButton")}
            </button>

            <button
              type="button"
              onClick={toggleMode}
              className={styles.toggleMode}
            >
              {t("auth.signup.haveAccount")} {t("auth.signup.loginHere")}
            </button>
          </form>
        )}
      </main>
      <footer className={styles.footer}>
        <p>{t("welcome.subtext")}</p>

        <section className={styles.logos}>
          <LogoJaerskulen />
          <LogoLesesenteret />
          <LogoForsknigsradet />
        </section>
      </footer>
    </>
  );
};

export default Welcome;
